<script setup>
import { ref, watch, onMounted, onBeforeUnmount, computed } from 'vue';
import { EditorState, Compartment } from '@codemirror/state';
import { EditorView, keymap } from '@codemirror/view';
import { basicSetup } from 'codemirror';
import { html, htmlLanguage } from '@codemirror/lang-html';
import { indentWithTab } from '@codemirror/commands';
import { indentUnit } from '@codemirror/language';
import {
  liquidCompletionSource,
  liquidIndentService,
} from '../composables/useLiquidCompletion';

const props = defineProps({
  modelValue: { type: String, default: '' },
  components: { type: Array, default: () => [] },
});

const emit = defineEmits(['update:modelValue']);

const langCompartment = new Compartment();

const LAYOUT_EXTRA_TAGS = {
  row: {
    attrs: { gap: ['0', '2', '4', '6', '8', '10', '12'] },
  },
  column: {
    attrs: {
      span: ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'],
    },
  },
};

const attrValuesForProp = def => {
  if (!def || typeof def !== 'object') return null;
  if (def.type === 'select' && def.options?.length) return def.options;
  if (def.type === 'boolean') return ['true', 'false'];
  return null;
};

const buildExtraTags = components => {
  const tags = { ...LAYOUT_EXTRA_TAGS };

  components.forEach(c => {
    if (!c?.tag_name) return;
    const attrs = {};
    Object.entries(c.props || {}).forEach(([key, def]) => {
      attrs[key] = attrValuesForProp(def);
    });
    if (c.tag_name === 'portal-tab' && !('name' in attrs)) {
      attrs.name = null;
    }
    tags[c.tag_name] = { attrs };
  });

  if (!tags['portal-tab']) {
    tags['portal-tab'] = { attrs: { name: null } };
  } else if (!('name' in (tags['portal-tab'].attrs || {}))) {
    tags['portal-tab'] = {
      attrs: { ...tags['portal-tab'].attrs, name: null },
    };
  }

  return tags;
};

const buildLangExtensions = tags => [
  html({ extraTags: tags }),
  htmlLanguage.data.of({ autocomplete: liquidCompletionSource }),
];

const extraTags = computed(() => buildExtraTags(props.components));

const editorContainer = ref(null);
let editorView = null;
// Track the last value we emitted so we can skip the round-trip update
let lastEmitted = '';

watch(
  () => props.modelValue,
  val => {
    if (!editorView) return;
    // If this is the value we just emitted, skip — CM already has it
    if (val === lastEmitted) return;
    const currentDoc = editorView.state.doc.toString();
    if (val !== currentDoc) {
      editorView.dispatch({
        changes: { from: 0, to: editorView.state.doc.length, insert: val },
      });
    }
  }
);

let resizeObserver = null;
let emitTimer = null;

const flushEmit = () => {
  if (!editorView) return;
  clearTimeout(emitTimer);
  emitTimer = null;
  const doc = editorView.state.doc.toString();
  lastEmitted = doc;
  emit('update:modelValue', doc);
};

watch(extraTags, tags => {
  if (!editorView) return;
  editorView.dispatch({
    effects: langCompartment.reconfigure(buildLangExtensions(tags)),
  });
});

onMounted(() => {
  lastEmitted = props.modelValue;

  const state = EditorState.create({
    doc: props.modelValue,
    extensions: [
      basicSetup,
      indentUnit.of('  '),
      keymap.of([indentWithTab]),
      liquidIndentService,
      langCompartment.of(buildLangExtensions(extraTags.value)),
      EditorView.updateListener.of(update => {
        if (update.docChanged) {
          const doc = update.state.doc.toString();
          lastEmitted = doc;
          // Debounce the emit so rapid typing doesn't cause churn
          clearTimeout(emitTimer);
          emitTimer = setTimeout(() => {
            emit('update:modelValue', doc);
          }, 150);
        }
      }),
      EditorView.theme({
        '&': {
          height: '100%',
          outline: 'none',
          fontSize: '13px',
        },
        '&.cm-focused': {
          outline: 'none',
        },
        '.cm-scroller': {
          height: '100%',
          overflow: 'auto',
          fontFamily:
            'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
        },
        '.cm-content': {
          minHeight: '0',
        },
        '.cm-gutters': {
          borderRight: '1px solid rgb(var(--slate-6))',
        },
      }),
      EditorView.lineWrapping,
    ],
  });

  editorView = new EditorView({
    state,
    parent: editorContainer.value,
  });

  resizeObserver = new ResizeObserver(() => {
    if (editorView) editorView.requestMeasure();
  });
  resizeObserver.observe(editorContainer.value);
});

onBeforeUnmount(() => {
  flushEmit();
  resizeObserver?.disconnect();
  editorView?.destroy();
  editorView = null;
});
</script>

<template>
  <div
    class="flex flex-col flex-1 min-h-0 overflow-hidden border-r border-n-slate-6"
  >
    <div
      class="flex items-center justify-between px-4 py-2 border-b border-n-slate-6 bg-n-slate-2 shrink-0"
    >
      <p class="text-xs font-semibold text-n-slate-11 uppercase tracking-wide">
        {{ $t('PLANNING.EVENT_PORTALS.EDITOR.CODE_PANEL.TITLE') }}
      </p>
      <span class="text-[10px] text-n-slate-9 font-mono">
        {{ $t('PLANNING.EVENT_PORTALS.EDITOR.CODE_PANEL.HINT') }}
      </span>
    </div>
    <div ref="editorContainer" class="flex-1 min-h-0 overflow-hidden" />
  </div>
</template>
